import React, { useState } from 'react';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import { getBank, updateBank, listDeposits, makeDeposit } from '../../api/banks';
import './BankRechargePage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Parametres > Recharger un Bank (demande explicite 2026-09-19) : la company
// cherche/filtre un bank (BankScopeField, meme composant que partout ailleurs
// dans l'app) puis peut activer le mode "solde prepaye" (Bank.is_balance) et y
// deposer de l'argent — une fois actif, le bank ne peut vendre que jusqu'a
// concurrence de son solde (cf. tickets/serializers.py cote backend). Page
// SEPAREE de BanqueDetailPage.jsx (demande explicite), pas une section
// ajoutee a cette derniere.
const BankRechargePage = () => {
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');
  const [bank, setBank] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadBank = (id) => {
    setLoading(true);
    setError('');
    Promise.all([getBank(id), listDeposits(id)])
      .then(([b, d]) => { setBank(b); setDeposits(d); })
      .catch(() => setError('Chargement impossible.'))
      .finally(() => setLoading(false));
  };

  const handleResolve = (id, name) => {
    setBankId(id);
    setBankName(name);
    if (id) {
      loadBank(id);
    } else {
      setBank(null);
      setDeposits([]);
    }
  };

  const handleToggleBalance = async () => {
    setToggling(true);
    setError('');
    try {
      const updated = await updateBank(bankId, { is_balance: !bank.is_balance });
      setBank(updated);
    } catch (err) {
      setError('Action impossible.');
    } finally {
      setToggling(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!amount || Number.isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Montant invalide.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await makeDeposit(bankId, { amount, note });
      setAmount('');
      setNote('');
      loadBank(bankId);
    } catch (err) {
      setError('Dépôt impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <p className="brg-breadcrumb">
        Ruta : <span className="bc-current">Parametres &gt;&gt; Recharger un Bank</span>
      </p>

      <div className="brg-card">
        <div className="brg-card-header">
          <span className="brg-card-title">Chercher un bank</span>
        </div>
        <div className="brg-card-body">
          <div className="brg-row">
            <label className="brg-label">Bank</label>
            <BankScopeField
              id={bankId}
              name={bankName}
              onResolve={handleResolve}
              inputClass="brg-input brg-input-sm"
              iconClass="brg-icon-btn"
              nameClass="brg-copy-name"
              errClass="brg-copy-name"
            />
          </div>
        </div>
      </div>

      {loading && <p className="brg-status">Chargement...</p>}
      {error && <p className="brg-error">{error}</p>}

      {bank && !loading && (
        <>
          <div className="brg-card">
            <div className="brg-card-header">
              <span className="brg-card-title">{bank.name} — Solde</span>
            </div>
            <div className="brg-card-body">
              <div className="brg-row">
                <label className="brg-label">Solde actuel</label>
                <span className="brg-balance">{money(bank.balance)}</span>
              </div>
              <label className="brg-checkbox-label">
                <input
                  type="checkbox"
                  checked={bank.is_balance}
                  disabled={toggling}
                  onChange={handleToggleBalance}
                />
                Mode solde prépayé actif (le bank ne peut vendre que jusqu'à ce solde)
              </label>
            </div>
          </div>

          <div className="brg-card">
            <div className="brg-card-header">
              <span className="brg-card-title">Nouveau dépôt</span>
            </div>
            <div className="brg-card-body">
              <form onSubmit={handleDeposit} className="brg-form">
                <div className="brg-row">
                  <label className="brg-label">Montant</label>
                  <input
                    className="brg-input brg-input-sm"
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="brg-row">
                  <label className="brg-label">Note</label>
                  <input
                    className="brg-input"
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optionnel"
                  />
                </div>
                <button type="submit" className="brg-submit-btn" disabled={submitting}>
                  {submitting ? 'Dépôt...' : 'Déposer'}
                </button>
              </form>
            </div>
          </div>

          <div className="brg-card">
            <div className="brg-card-header">
              <span className="brg-card-title">Historique des dépôts</span>
            </div>
            <div className="brg-card-body">
              {deposits.length === 0 ? (
                <p className="brg-empty">Aucun dépôt pour ce bank.</p>
              ) : (
                <table className="brg-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Montant</th>
                      <th>Note</th>
                      <th>Par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deposits.map((d) => (
                      <tr key={d.id}>
                        <td>{new Date(d.created_at).toLocaleString()}</td>
                        <td className="brg-money">{money(d.amount)}</td>
                        <td>{d.note || '—'}</td>
                        <td>{d.created_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default BankRechargePage;
